<template>
  <div>
    <div class="hidden help-title">Cross Tabulation Information</div>
    <p>
      Cross tabulations allow you to generate tables with frequencies across two or more categorical variables.  For example, imagine that you are interested in learning whether men exercise more than women.  The BRFSS dataset includes two 2-level variables to help answer this question.  Sex is answered "male" or "female," and exercise in past 30 days is answered "yes" or "no."
    </p>
    <h3>Creating a cross tabulation</h3>
    <p>
      To generate a table to help answer this question, follow these steps.  
      Note that "My Analysis" on the bottom right of your screen is updated as you make selections.
      <ol>
        <li>Select "Cross Tabulation" on the main page or in the menu on the left.</li>
        <li>Select the year 2011 and click the "Next" button.</li>
        <li>Select any location and click the "Next" button.  Do not select "All Locations" at this time, as the length of time to generate the crosstab might be longer than you care to wait.</li>
        <li>On the "Select Row Variable" step</li>
        <ul>
          <li>Select the topic "Exercise"</li>
          <li>Select the variable "Exercised in past 30 days."</li>
          <li>Click the "Next" button to continue.</li>
        </ul>
        <li>On the "Select a Column Variable" step</li>
        <ul>
          <li>Select the topic "Demographic Information"</li>
          <li>Select the variable "Respondent's Sex."</li>
          <li>Click the "Next" button to continue.</li>
        </ul>
        <li>On the "Select Control Variables" step, leave both options to “Include a Control Variable” and “Include an Additional Control Variable” unselected. 
          Click the "Next" button.</li>
        <li>"Check" and "Uncheck" those statistics that are of interest, or leave the check marks that appear already. The minimum statistics will already be included. Click the "Next" button.</li>
        <li>Click on "Run Report"</li>
        <li>Review the resulting table on your screen.</li>
      </ol>
    </p>
    <p>
      Note: You could select sex as the row variable and exercise as the column variable, 
      but some analysts prefer reading the independent variable in columns and dependent variable in rows.
    </p>
    <h3>Adding a control variable</h3>
    <p>
      Sometimes you may want to introduce a third variable (control variable) to your analysis.
      For example, imagine that you want to learn whether a person's sex affects exercise, and whether the influence of a person's sex increases
      (or decreases) with age.
      To answer this question, follow similar steps as above, but add a single control. On the "Select Control Variables" step:
    </p>
    <ol>
      <li>Click the "Include a Control Variable" check box</li>
      <ul>
        <li>Select topic "Demographic Information"</li>
        <li>Select the variable "Reported age (18-64, 65+)."</li>
      </ul>
    </ol>

    <p>
      Once you click on "Next" and "Run Report", you will create the original totals table for all ages, followed by two tables containing data for individuals 18-64 years old and 65 and older.  
      A selection box is provided to show only the wanted tables.
    </p>
    <h3>Adding a second control variable</h3>
    <p>
      Finally, you may be interested in introducing a second control variable to refine your analysis even further.
      Perhaps your analysis demonstrated that a person's sex affects exercise, and the influence of a person's sex increases for older adults--but now you wonder whether that age differential is moderated by cancer.
      To answer this question, follow the same steps, adding two controls.  On the "Select Control Variables" step:
    </p>
  <ol>
      <li>Click the "Include a Control Variable" check box</li>
      <ul>
        <li>Select topic "Demographic Information"</li>
        <li>Select the variable "Reported age (18-64, 65+)"</li>
      </ul>
      <li>Click the "Include an Additional Control Variable" check box</li>
      <ul>
        <li>Select topic "Chronic Health Conditions"</li>
        <li>Select the variable "Ever told you had any other types of cancer excluding skin cancer."</li>
        <li>Click the "Next" button, then "Run Report"</li>
      </ul>
    </ol>
    <p>
      This will produce 9 tables:
    </p>
    <ul>
      <li>The totals table, containing data for all respondents</li>
      <li>Two tables, one each for the two age groups, but for all respondents regardless of their answer to the cancer question.</li>
      <li>Two tables, one each for "Yes" and "No to the cancer question, but for all age groups</li>
      <li>
        Four tables for:
        <ul>
          <li>Age Group: Age 18 to 64, Ever Had Cancer: Yes</li>
          <li>Age Group: Age 18 to 64, Ever Had Cancer: No</li>
          <li>Age Group: Age 65 or older, Ever Had Cancer: Yes</li>
          <li>Age Group: Age 65 or older, Ever Had Cancer: No</li>
        </ul>
      </li>
    </ul>
    <p>
      You probably can begin to see how the filter can be useful in complex scenarios to find the appropriate table.
    </p>
    <h3>Modifications</h3>
    <p>
      Every time you want to return to the cross-tabulation page to add or change variables,
      use the "Back" button to find the appropriate step.
      Alternatively, use the back arrow on your browser to find the appropriate step.
      Your original selected variables will be available for you to modify as needed.
    </p>
    <h3>Download Results</h3>
    <p>
      If you want to download results to a spreadsheet, click on the button "Download Results." 
      This will download the results in an Excel format.
      The first tab is an information tab that describes the spreadsheet.
      If you did not select any control variables, the second tab contains the cross tabulation.
      Otherwise, the second tab is the total cross tabulation, and subsequent tabs contain an individual cross tabulation for each control variable level.
    </p>
  </div>
</template>

<script>
import current from "@/scripts/current";

export default {
  data: function () {
    current.initializeCrossTabulationHelp();
    return {};
  },
  methods: {},
};
</script>

<style scoped>
</style>
